import React from "react";
import moment from "moment";

import FormSection from "../../FundForm/FormSection";
import Notice from "../../FundForm/Notice";
import WindowTimeline from "../../FundForm/WindowTimeline";
import { Calendar } from "../../FundForm/icons";
import {
  planWindows,
  WINDOW_UNIT_NAME,
  WindowUnit,
  WINDOWS_SHOWN,
} from "../../FundForm/withdrawalWindows";
import { formatDate, TSavedFund } from "../previewFund";
import formStyles from "../../FundForm/fundForm.module.scss";
import styles from "../previewFund.module.scss";

interface IWithdrawalWindowsCard {
  fund: TSavedFund;
  step: string;
}

/**
 * When a Habit Save customer can take money out without a charge.
 *
 * The windows are counted from the first subscription day, so the dates are
 * ones a real plan on this fund could get — the form counts from today,
 * because there it has no saved fund to count from.
 */
export default function WithdrawalWindowsCard({
  fund,
  step,
}: IWithdrawalWindowsCard) {
  const interval = fund.withdrawal_interval?.value;
  const unit = fund.withdrawal_interval_unit?.value ?? WindowUnit.Monthly;
  const grace = fund.grace_periods?.value;
  const start = fund.start_date ? moment(fund.start_date) : moment();
  const windows = planWindows({ start, interval, unit, grace });

  return (
    <FormSection
      id="withdrawal-windows"
      title="Withdrawal windows"
      subtitle="When a Habit Save customer can take money out without a charge."
      icon={<Calendar color="#713fff" />}
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className={formStyles.tabChip}>Habit Save only</span>
          <span className={formStyles.sectionMeta}>{step}</span>
        </div>
      }
    >
      <div className={styles.stackLoose}>
        <dl className={styles.tiles}>
          <div className={styles.tile}>
            <dt className={styles.detailLabel}>A window opens every</dt>
            <dd className={styles.tileValue}>
              {interval ? `${interval} ${WINDOW_UNIT_NAME[unit]}` : "Not set"}
            </dd>
          </div>
          <div className={styles.tile}>
            <dt className={styles.detailLabel}>And stays open for</dt>
            <dd className={styles.tileValue}>
              {grace === null || grace === undefined
                ? "Not set"
                : `${grace} ${grace === 1 ? "day" : "days"}`}
            </dd>
          </div>
          <div className={styles.tile}>
            <dt className={styles.detailLabel}>First window</dt>
            <dd className={styles.tileValue}>
              {windows.length
                ? formatDate(windows[0].opens.toISOString())
                : "Not set"}
            </dd>
          </div>
        </dl>

        {windows.length ? (
          <div>
            <div className={formStyles.previewTitle}>
              The first {WINDOWS_SHOWN} windows, for a plan opened on the first
              subscription day
            </div>
            <WindowTimeline start={start} windows={windows} />
          </div>
        ) : (
          <Notice tone="info">
            No interval is set, so there are no windows to show.
          </Notice>
        )}
      </div>
    </FormSection>
  );
}
